import {
  Shield,
  Lock,
  KeyRound,
  Eye,
  Globe,
  Database,
  ScanLine,
  RefreshCw,
  BadgeCheck,
} from "lucide-react";
import { BRAND } from "@/lib/constants";

const CARDS = [
  {
    icon: Lock,
    title: "Credential encryption",
    description:
      "Your portal credentials are sealed with Fernet authenticated encryption (AES-128-CBC + HMAC-SHA256) under a key unique to your account — never stored in plaintext anywhere in our systems.",
  },
  {
    icon: KeyRound,
    title: "Key custody",
    description:
      "Your per-account key is derived with PBKDF2-HMAC-SHA256 from a master key held in Azure Key Vault and fetched at runtime by a managed identity. The key never lives in our application code or environment.",
  },
  {
    icon: Eye,
    title: "Read-only access & MFA",
    description:
      "Elementar connects to your portals read-only — funds can never move — and two-factor authentication is handled for you. Connections originate from a single static IP (172.161.97.87) your providers can whitelist.",
  },
  {
    icon: Globe,
    title: "European data residency",
    description:
      "All servers, documents, and databases run on Microsoft Azure in Europe (EU + Switzerland). No client data is processed outside Europe.",
  },
  {
    icon: Database,
    title: "Isolation & erasure",
    description:
      "Each client is a fully isolated tenant — your data is never commingled. Documents and databases are encrypted at rest (AES-256), and on offboarding your credentials are cryptographically erased.",
  },
  {
    icon: ScanLine,
    title: "Application hardening",
    description:
      "Logins use bcrypt password hashing and hardened, signed session tokens; request and upload sizes are capped against abuse; traffic is HSTS-enforced; and every code change passes automated secret-scanning before it can ship.",
  },
  {
    icon: RefreshCw,
    title: "Continuous patching",
    description:
      "We continuously scan our software dependencies for known vulnerabilities and apply high-severity security patches promptly. Every change ships through a staging environment and an automated test suite before it reaches production.",
  },
  {
    icon: BadgeCheck,
    title: "Compliance",
    description:
      "Elementar is Swiss-domiciled, operating under the Swiss nFADP and the EU GDPR. We are not yet SOC 2 / ISO 27001 certified — full controls are in our due-diligence sheet.",
  },
];

export default function SecurityTab() {
  return (
    <div className="space-y-5 w-full">
      {/* Header callout */}
      <div
        className="rounded-xl px-6 py-5 flex items-start gap-4"
        style={{ backgroundColor: BRAND.soft }}
      >
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
          style={{ backgroundColor: BRAND.bg }}
        >
          <Shield className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="font-semibold text-gray-900">Your data stays in Europe</p>
          <p className="text-sm text-gray-600 mt-1">
            Everything runs on Microsoft Azure infrastructure, in European data centres.
            No client data is processed outside Europe.
          </p>
        </div>
      </div>

      {/* Control-family card grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {CARDS.map(({ icon: Icon, title, description }) => (
          <div
            key={title}
            className="rounded-xl border border-gray-100 bg-white px-5 py-4 flex items-start gap-4 shadow-sm"
          >
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
              style={{ backgroundColor: BRAND.soft }}
            >
              <Icon className="w-4 h-4" style={{ color: BRAND.text }} />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">{title}</p>
              <p className="text-sm text-gray-600 mt-1 leading-relaxed">{description}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Security overview link */}
      <p className="text-sm text-gray-700">
        <a
          href="/elementar/security"
          className="underline"
          style={{ color: BRAND.text }}
        >
          Read our full security overview and due-diligence sheet &rarr;
        </a>
      </p>

      {/* Contact line */}
      <p className="text-sm text-gray-500">
        Questions about data handling?{" "}
        <a
          href="mailto:eilon@elementarai.com"
          className="underline"
          style={{ color: BRAND.text }}
        >
          eilon@elementarai.com
        </a>
      </p>
    </div>
  );
}
